import React from "react";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import {
  NEXTHOP_RETRY_DEFAULT_ATTEMPTS,
  NEXTHOP_RETRY_DEFAULT_INTERVAL_SECONDS,
  NEXTHOP_RETRY_MAX_ATTEMPTS,
  NEXTHOP_RETRY_MAX_INTERVAL_SECONDS,
  NEXTHOP_RETRY_MIN_ATTEMPTS,
  NEXTHOP_RETRY_MIN_INTERVAL_SECONDS,
  NEXTHOP_RETRY_MIN_VERSION,
  type NexthopRetryFormValues,
} from "../../../../../helpers/cloudPropagationHelpers";

// Mirrors DYNAMIC_TAG_VERSION_TOOLTIP / DYNAMIC_TAG_DISABLED_LABEL in InboundContent —
// an unsupported reflector gets the same tooltip + "Unsupported version" tag the
// "Dynamic detection with tag" option shows.
const VERSION_TOOLTIP = `Requires reflector version ${NEXTHOP_RETRY_MIN_VERSION} or later`;
const UNSUPPORTED_VERSION_LABEL = "Unsupported version";

/**
 * Everything the operator needs to understand the two fields lives here rather than
 * under them, so the row stays one line: what retry does, the accepted ranges, and
 * the per-rule scope (the reflector keys its pending work per rule — a sibling rule
 * of the same neighbour can trigger an earlier pass without spending this rule's
 * attempts). See docs/nexthop-retry-frontend-integration.md.
 */
const RETRY_TOOLTIP = [
  "If the next-hop IP is not yet visible in AWS (e.g. a pod being rescheduled), the reflector retries resolution every N seconds up to M times before giving up.",
  `Interval ${NEXTHOP_RETRY_MIN_INTERVAL_SECONDS}–${NEXTHOP_RETRY_MAX_INTERVAL_SECONDS}s, up to ${NEXTHOP_RETRY_MAX_ATTEMPTS} attempts.`,
  "Applies to this rule. A retry may also be triggered earlier by another rule of the same BGP neighbor; attempts are counted per rule.",
].join(" ");

interface NexthopRetrySettingsProps {
  values: NexthopRetryFormValues;
  /** Receives only the changed retry fields; the caller merges them into its form state. */
  onChange: (patch: NexthopRetryFormValues) => void;
  /**
   * True when the reflector (or any instance of the cluster) is below
   * NEXTHOP_RETRY_MIN_VERSION. The row then shows only the label, the version tag
   * and its tooltip — there is nothing to configure on a reflector that would
   * ignore the setting. Mirrors how "Dynamic detection with tag" is gated (CTX-163).
   */
  disabled?: boolean;
}

/**
 * Parses a number input. Both fields are whole numbers — seconds and attempts —
 * so a typed decimal is truncated at the separator (`6.5` → `6`, not `65`), and an
 * empty/non-numeric field becomes `undefined` rather than 0 so the operator can
 * clear it while typing; validation rejects that on save.
 */
const parseRetryNumber = (raw: string): number | undefined => {
  const leadingDigits = raw.match(/^\s*(\d+)/)?.[1];
  return leadingDigits === undefined ? undefined : Number.parseInt(leadingDigits, 10);
};

/**
 * "Advanced settings → Retry": how often, and how many times, the reflector
 * re-attempts next-hop resolution before dropping the route (CTX-181).
 * Rendered once per VRF in Telco mode and once on the flat Enterprise form,
 * the same way Route persistence is.
 */
const NexthopRetrySettings: React.FC<NexthopRetrySettingsProps> = ({
  values,
  onChange,
  disabled = false,
}) => {
  // An untouched rule carries no retry fields at all (CTX-180: `null` means "not
  // configured", and the reflector applies its own defaults). Those defaults are
  // shown as placeholders, and the first edit materialises all three fields so a
  // rule the operator never opened is never written back with values.
  const configured = values.nexthopRetryEnabled !== undefined;
  const enabled = values.nexthopRetryEnabled ?? true;
  // Once configured, a number can legitimately be absent — the operator cleared the
  // input mid-edit — and must stay empty so validation and the field agree.
  const interval = configured
    ? values.nexthopRetryIntervalSeconds
    : NEXTHOP_RETRY_DEFAULT_INTERVAL_SECONDS;
  const attempts = configured ? values.nexthopRetryAttempts : NEXTHOP_RETRY_DEFAULT_ATTEMPTS;

  const applyEdit = (patch: NexthopRetryFormValues) =>
    onChange({
      nexthopRetryEnabled: enabled,
      nexthopRetryIntervalSeconds: interval,
      nexthopRetryAttempts: attempts,
      ...patch,
    });

  const intervalOutOfRange =
    interval !== undefined &&
    (interval < NEXTHOP_RETRY_MIN_INTERVAL_SECONDS || interval > NEXTHOP_RETRY_MAX_INTERVAL_SECONDS);
  const attemptsOutOfRange =
    attempts !== undefined &&
    (attempts < NEXTHOP_RETRY_MIN_ATTEMPTS || attempts > NEXTHOP_RETRY_MAX_ATTEMPTS);
  // Only meaningful once both fields hold a number, so a half-typed form shows no total.
  const total = interval !== undefined && attempts !== undefined ? interval * attempts : undefined;

  return (
    <div>
      <div className="text-semantic-text-primary mb-2 text-body-large">Advanced settings</div>
      <div className="text-semantic-text-secondary text-body-small mb-2">
        Optional fine-tuning for how the rule reacts to failures.
      </div>
      <div className="flex items-end gap-4 flex-wrap">
        <div className="flex items-center gap-2 h-10">
          <span className="text-semantic-text-primary text-body">Retry</span>
          <GenericTooltip
            text={disabled ? VERSION_TOOLTIP : RETRY_TOOLTIP}
            position="top"
            width="24rem"
          />
          {disabled ? (
            <GenericTag label={UNSUPPORTED_VERSION_LABEL} variant="neutral" size="small" />
          ) : (
            <>
              <GenericSwitch
                checked={enabled}
                onChange={(v) => applyEdit({ nexthopRetryEnabled: v })}
              />
              <span className="text-semantic-text-secondary text-body-small w-6">
                {enabled ? "On" : "Off"}
              </span>
            </>
          )}
        </div>

        {!disabled && (
          <>
            <div className="w-32">
              <GenericInput
                label="Interval"
                type="number"
                unit="sec"
                value={interval ?? ""}
                onChange={(v) => applyEdit({ nexthopRetryIntervalSeconds: parseRetryNumber(v) })}
                hasError={intervalOutOfRange}
                disabled={!enabled}
                className="text-body"
              />
            </div>
            <div className="w-32">
              <GenericInput
                label="Attempts"
                type="number"
                unit="max"
                value={attempts ?? ""}
                onChange={(v) => applyEdit({ nexthopRetryAttempts: parseRetryNumber(v) })}
                hasError={attemptsOutOfRange}
                disabled={!enabled}
                className="text-body"
              />
            </div>
            {/* Hidden while retry is off — there is no window to advertise.
                Grey ("default") rather than the brand-tinted "neutral": this is a
                derived read-out, not a status the operator should read as a state. */}
            {enabled && total !== undefined && (
              <div className="h-10 flex items-center">
                <GenericTag label={`${total}s total`} variant="default" size="small" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NexthopRetrySettings;
